import { ulid } from 'ulid';
import { z } from 'zod';
/**
 * Ids are opaque, stable forever, and comfortably under the agreement's 128
 * characters (C6). ULIDs rather than sequence integers, specifically so that a
 * data migration cannot renumber a `resource_id` the partner has stored —
 * availability is exclusive per resource, so a reused resource id would silently
 * merge two rooms' calendars.
 */
export const ID_PREFIX = {
    appointment: 'ap',
    client: 'cl',
    service: 'sv',
    resource: 'rs',
    hold: 'hd',
    event: 'ev',
    apiKey: 'ak',
    webhookEndpoint: 'we',
    delivery: 'wd',
    certificate: 'ct',
    product: 'pr',
    staff: 'st',
    /** A line on an appointment or a settlement. */
    line: 'ln',
    /** A preference, allergy or note on a client. */
    preference: 'pf',
    /** A closure on the calendar. */
    blackout: 'bo',
    /** A checkout document. */
    settlement: 'se',
    /** A row in the operator audit trail. */
    audit: 'au',
    /** A choice group on a menu item — "Temperature", "Choose two sides". */
    modifierGroup: 'mg',
    /** One choice inside a group — "Medium rare", "+ avocado". */
    modifier: 'md',
};
export function newId(prefix) {
    return `${prefix}_${ulid()}`;
}
const idSchema = (prefix) => z.string().regex(new RegExp(`^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`), `must be a ${prefix}_ id`);
export const AppointmentId = idSchema(ID_PREFIX.appointment);
export const ClientId = idSchema(ID_PREFIX.client);
export const ServiceId = idSchema(ID_PREFIX.service);
export const ResourceId = idSchema(ID_PREFIX.resource);
export const HoldId = idSchema(ID_PREFIX.hold);
/**
 * A tenant id is **not** generated. The partner sends `t_lovespa_frenchtown` on
 * every request (agreement section 2), so the operator chooses it at creation
 * and it is stable forever. It is the one id in the system that is human-legible
 * on purpose.
 */
export const TenantId = z
    .string()
    .regex(/^t_[a-z0-9][a-z0-9_]{1,120}$/, 'must be a t_ slug, lower case');
/**
 * `tenants.slug` — a **DNS label**, not a decoration.
 *
 * Spec §6.4.2 gives every tenant `{slug}.book.yourstore.<tld>` under one
 * wildcard certificate, and `app.tenant_for_host()` (migration 0010) resolves
 * that form by stripping the suffix and looking the remaining label up here. A
 * label is what RFC 1123 says it is: lower-case letters, digits and hyphens, no
 * leading or trailing hyphen, at most 63 characters. `tenant_domains` has
 * enforced exactly this shape since 0010 for the hostnames an operator types;
 * the same rule belongs on the slug those hostnames are *derived* from, and
 * since 0016 it is a `CHECK` constraint on `tenants` too.
 */
export const TenantSlug = z
    .string()
    .max(63, 'a DNS label is at most 63 characters')
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'must be a DNS label: a-z, 0-9 and hyphens');
/**
 * The default hostname label for a tenant id — **the one derivation**.
 *
 * A tenant id is `t_demo_spa`; a hostname label cannot carry an underscore, so
 * `demo_spa.book.example.com` is not a name DNS can hold and not a name the
 * wildcard certificate can cover. The default booking site of §6.4.2 was
 * therefore unreachable by construction for every tenant whose id had more than
 * one word — which is all of them. Strip the `t_`, turn underscores into
 * hyphens, and the address exists.
 *
 * This function is the only place that transformation happens. It used to be
 * `id.slice(2)` copied into six files, which is how the underscore survived:
 * nobody owned the rule, so nobody noticed it was wrong.
 *
 * Deliberately **not** a sanitizer. It does not lower-case, strip or truncate,
 * because a tenant id is already constrained to `^t_[a-z0-9][a-z0-9_]{1,120}$`
 * and anything this would have to "fix" is a caller passing something that is
 * not a tenant id. Two ids can only collide here if one already contained a
 * hyphen, which the id constraint forbids — so the mapping is injective over
 * legal ids, and a slug that is not a legal label (a 120-character id) is
 * refused by the constraint rather than silently shortened into someone else's.
 */
export function slugForTenantId(id) {
    return (id.startsWith('t_') ? id.slice('t_'.length) : id).replace(/_/g, '-');
}
/**
 * Ids we accept from the partner and hand back untouched. We store them and
 * never parse them (agreement section 2), so the only rule is a length bound.
 */
export const OpaqueRef = z.string().min(1).max(128);
//# sourceMappingURL=ids.js.map